import React, {
  createContext,
  useContext,
  useState,
  useRef,
  ReactNode,
  useEffect,
} from 'react'

type PropertyType =
  | 'house_apartment'
  | 'house'
  | 'apartment'
  | 'commercial'
  | 'plot'

export interface PurchaseMarketService {
  getPriceEvolutionData: (filters: any) => Promise<any>
  getNewVsResaleData: (filters: any) => Promise<any>
  getPricePerRoomData: (filters: any) => Promise<any>
  getPriceM2PerRoomData: (filters: any) => Promise<any>
  getDistributionData: (filters: any) => Promise<any>
}

interface PurchaseMarketContextType {
  filters: {
    locationType: 'postal_code' | 'city' | 'district'
    propertyType: PropertyType
    location: string | null
    propertyCategory: string[]
  }
  updateLocationType: (type: 'postal_code' | 'city' | 'district') => void
  updatePropertyType: (type: PropertyType) => void
  getLocationDisplay: () => string
  service: PurchaseMarketService
}

const PurchaseMarketContext = createContext<
  PurchaseMarketContextType | undefined
>(undefined)

interface PurchaseMarketProviderProps {
  children: ReactNode
  landData: any
  service: PurchaseMarketService
  initialPropertyType?: PropertyType
  initialLocationType?: 'postal_code' | 'city' | 'district'
  onFilterChange?: (filters: {
    locationType: 'postal_code' | 'city' | 'district'
    propertyType: PropertyType
    location: string | null
    propertyCategory: string[]
  }) => void
}

export const PurchaseMarketProvider: React.FC<PurchaseMarketProviderProps> = ({
  children,
  landData,
  service,
  initialPropertyType,
  initialLocationType,
  onFilterChange,
}) => {
  const [locationType, setLocationType] = useState<
    'postal_code' | 'city' | 'district'
  >(initialLocationType || 'postal_code')
  const isInitialRender = useRef(true)
  const [propertyType, setPropertyType] = useState<PropertyType>(
    initialPropertyType || 'house_apartment'
  )

  const getLocation = (): string | null => {
    if (!landData) return null

    switch (locationType) {
      case 'postal_code':
        return landData.address_postal_code_name || null
      case 'city':
        return landData.municipality_name || null
      case 'district':
        return landData.district_name || null
      default:
        return null
    }
  }

  const getLocationDisplay = (): string => {
    if (!landData) return 'N/A'

    // Return the selected location level
    const location = getLocation()
    return location || 'N/A'
  }

  const getPropertyCategory = (): string[] => {
    switch (propertyType) {
      case 'house_apartment':
        return ['House', 'Apartment']
      case 'apartment':
        return ['Apartment']
      case 'house':
        return ['House']
      case 'commercial':
        return ['Commercial']
      case 'plot':
        return ['Plot']
      default:
        return []
    }
  }

  const filters = {
    locationType,
    propertyType,
    location: getLocation(),
    propertyCategory: getPropertyCategory(),
  }

  const updateLocationType = (type: 'postal_code' | 'city' | 'district') => {
    setLocationType(type)
  }

  const updatePropertyType = (type: PropertyType) => {
    setPropertyType(type)
  }

  // Notify parent when filters change (skip initial render)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    onFilterChange?.(filters)
  }, [locationType, propertyType])

  return (
    <PurchaseMarketContext.Provider
      value={{
        filters,
        updateLocationType,
        updatePropertyType,
        getLocationDisplay,
        service,
      }}
    >
      {children}
    </PurchaseMarketContext.Provider>
  )
}

export const usePurchaseMarket = () => {
  const context = useContext(PurchaseMarketContext)
  if (!context) {
    throw new Error(
      'usePurchaseMarket must be used within a PurchaseMarketProvider'
    )
  }
  return context
}
